import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Trash2, Plus, Save, X } from 'lucide-react';
import RawDataTable from './RawDataTable';
import type { RawDataTableProps } from '../types/table.types';
import EditableCell from './EditableCell';
import { ColumnDef } from '../types/table.types';
import { CrudError } from '../types/crud.types';
import { useToast } from '../hooks/useToast';
import ToastContainer from './ToastContainer';
import ConfirmDialog from './ConfirmDialog';

export interface InlineEditTableProps extends Omit<RawDataTableProps, 'onRowClick'> {
  entityType: 'articles' | 'sales' | 'stock' | 'suppliers' | 'article-suppliers' | 'lifecycles';
  databaseId: number | string;
  onUpdate: (id: string | number, data: Record<string, any>) => Promise<any>;
  onCreate?: (data: Record<string, any>) => Promise<any>;
  onDelete?: (id: string | number) => Promise<void>;
  getRowId: (row: any) => string | number;
  editableColumns?: string[]; // Columns that can be edited (if empty, all columns are editable)
  readOnlyColumns?: string[]; // Columns that cannot be edited (e.g., 'id', 'created_at')
  columnTypes?: Record<string, 'text' | 'number' | 'date' | 'select'>; // Column type overrides
  columnOptions?: Record<string, { value: any; label: string }[]>; // Options for select columns
  requiredColumns?: string[]; // Columns that are required
  onRefresh?: () => void; // Callback to refresh data after CRUD operations
  showCreateButton?: boolean;
  showDeleteButton?: boolean;
  deleteConfirmMessage?: (row: any) => string; // Custom delete confirmation message
}

const InlineEditTable: React.FC<InlineEditTableProps> = ({
  entityType,
  databaseId,
  data,
  columns,
  onUpdate,
  onCreate,
  onDelete,
  getRowId,
  editableColumns = [],
  readOnlyColumns = ['id', 'created_at', 'updated_at'],
  columnTypes = {},
  columnOptions = {},
  requiredColumns = [],
  onRefresh,
  showCreateButton = true,
  showDeleteButton = true,
  deleteConfirmMessage,
  ...rawTableProps
}) => {
  const { toasts, success, error, removeToast } = useToast();
  const [editingRows, setEditingRows] = useState<Set<string | number>>(new Set());
  const [savingRows, setSavingRows] = useState<Set<string | number>>(new Set());
  const [deletingRows, setDeletingRows] = useState<Set<string | number>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{
    open: boolean;
    row: any;
    rowId: string | number;
  }>({ open: false, row: null, rowId: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Listen for custom event to show create form from external button
  useEffect(() => {
    const handleShowCreateForm = () => {
      if (onCreate) {
        setShowCreateForm(true);
      }
    };
    window.addEventListener('showCreateForm', handleShowCreateForm);
    return () => {
      window.removeEventListener('showCreateForm', handleShowCreateForm);
    };
  }, [onCreate]);
  const [newRowData, setNewRowData] = useState<Record<string, any>>({});
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  // Determine which columns are editable
  const isColumnEditable = useCallback(
    (columnKey: string): boolean => {
      if (readOnlyColumns.includes(columnKey)) return false;
      if (editableColumns.length > 0) return editableColumns.includes(columnKey);
      return true;
    },
    [editableColumns, readOnlyColumns]
  );

  // Get column type
  const getColumnType = useCallback(
    (columnKey: string): 'text' | 'number' | 'date' | 'select' => {
      if (columnTypes[columnKey]) return columnTypes[columnKey];
      
      // Auto-detect from data
      if (data && data.length > 0) {
        const sampleValue = data[0]?.[columnKey];
        if (typeof sampleValue === 'number') return 'number';
        if (columnKey.includes('date') || columnKey.includes('Date')) return 'date';
      }
      
      return 'text';
    },
    [columnTypes, data]
  );

  // Handle cell update
  const handleCellUpdate = useCallback(
    async (row: any, columnKey: string, value: any) => {
      const rowId = getRowId(row);
      
      // Check if column is editable
      if (!isColumnEditable(columnKey)) {
        error(`La colonne "${columnKey}" n'est pas modifiable`);
        return;
      }

      setSavingRows((prev) => new Set(prev).add(rowId));

      try {
        // Prepare update data - send entire row
        const updateData = { ...row, [columnKey]: value };
        
        await onUpdate(rowId, updateData);
        
        success(`${columnKey} mis à jour avec succès`);
        
        // Refresh data
        if (onRefresh) {
          onRefresh();
        }
      } catch (err: any) {
        const errorMessage = err?.error || `Erreur lors de la mise à jour de ${columnKey}`;
        error(errorMessage);
      } finally {
        setSavingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
      }
    },
    [getRowId, isColumnEditable, onUpdate, onRefresh, success, error]
  );

  // Handle delete
  const handleDelete = useCallback(
    async (row: any) => {
      if (!onDelete) return;
      
      const rowId = getRowId(row);
      setDeletingRows((prev) => new Set(prev).add(rowId));

      try {
        await onDelete(rowId);
        success('Élément supprimé avec succès');
        
        if (onRefresh) {
          onRefresh();
        }
      } catch (err: any) {
        const errorMessage = err?.error || 'Erreur lors de la suppression';
        error(errorMessage);
      } finally {
        setDeletingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
        setShowDeleteConfirm({ open: false, row: null, rowId: '' });
      }
    },
    [onDelete, getRowId, onRefresh, success, error]
  );

  // Handle create
  const handleCreate = useCallback(
    async () => {
      if (!onCreate) return;

      // Validate required fields
      const errors: Record<string, string> = {};
      requiredColumns.forEach((col) => {
        if (!newRowData[col] || newRowData[col] === '') {
          errors[col] = 'Ce champ est requis';
        }
      });

      if (Object.keys(errors).length > 0) {
        setCreateErrors(errors);
        return;
      }

      setCreating(true);
      setCreateErrors({});

      try {
        await onCreate(newRowData);
        success('Élément créé avec succès');
        setNewRowData({});
        setShowCreateForm(false);
        
        if (onRefresh) {
          onRefresh();
        }
      } catch (err: any) {
        const errorMessage = err?.error || 'Erreur lors de la création';
        error(errorMessage);
      } finally {
        setCreating(false);
      }
    },
    [onCreate, newRowData, requiredColumns, onRefresh, success, error]
  );

  // Enhanced columns with editable cells
  const enhancedColumns = useMemo<ColumnDef[]>(() => {
    if (!columns) return [];
    const cols: ColumnDef[] = columns;

    return cols.map((column): ColumnDef => ({
      ...column,
      render: (value: any, row: any) => {
        const rowId = getRowId(row);
        const isSaving = savingRows.has(rowId);
        const isDeleting = deletingRows.has(rowId);
        const isEditable = isColumnEditable(column.key);

        // Show loading state
        if (isSaving || isDeleting) {
          return (
            <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
              <span>{isSaving ? 'Sauvegarde...' : 'Suppression...'}</span>
            </div>
          );
        }

        // Render editable cell
        if (isEditable) {
          return (
            <EditableCell
              value={value}
              onSave={(newValue) => handleCellUpdate(row, column.key, newValue)}
              type={getColumnType(column.key)}
              options={columnOptions[column.key]}
              required={requiredColumns.includes(column.key)}
              className={column.className}
            />
          );
        }

        // Render read-only cell with proper formatting
        const formatReadOnlyValue = (val: any, colKey: string): React.ReactNode => {
          if (val === null || val === undefined || val === '') {
            return <span className="text-gray-400 italic">—</span>;
          }
          
          // Format dates - check column type or detect ISO date/datetime strings
          const columnType = getColumnType(colKey);
          const isDateColumn = columnType === 'date';
          const isDateString = typeof val === 'string' && (
            /^\d{4}-\d{2}-\d{2}/.test(val) || // YYYY-MM-DD format
            /^\d{4}-\d{2}-\d{2}T/.test(val)  // ISO datetime format
          );
          
          if (isDateColumn || isDateString) {
            try {
              const date = new Date(val);
              if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('fr-FR');
              }
            } catch {
              // Fall through to string conversion
            }
          }
          
          return String(val);
        };
        
        return (
          <div className="px-4 py-3 text-sm text-gray-900">
            {formatReadOnlyValue(value, column.key)}
          </div>
        );
      },
    }));
  }, [
    columns,
    getRowId,
    savingRows,
    deletingRows,
    isColumnEditable,
    getColumnType,
    columnOptions,
    requiredColumns,
    handleCellUpdate,
  ]);

  // Add actions column
  const columnsWithActions = useMemo<ColumnDef[]>(() => {
    if (!showDeleteButton) return enhancedColumns;

    return [
      ...enhancedColumns,
      {
        key: '_actions',
        label: 'Actions',
        sortable: false,
        align: 'center',
        width: '100px',
        render: (_value: any, row: any) => {
          const rowId = getRowId(row);
          const isDeleting = deletingRows.has(rowId);

          if (isDeleting) {
            return (
              <div className="px-4 py-3 text-sm text-gray-500 flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
              </div>
            );
          }

          return (
            <div className="px-4 py-3 flex items-center justify-center gap-2">
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm({
                      open: true,
                      row,
                      rowId,
                    });
                  }}
                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title="Supprimer"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          );
        },
      },
    ];
  }, [enhancedColumns, showDeleteButton, getRowId, deletingRows, onDelete]);

  // Create form fields
  const createFormFields = useMemo<ColumnDef[]>(() => {
    if (!columns || columns.length === 0) return [];
    const cols: ColumnDef[] = columns;
    return cols.filter((col) => {
      // Don't show read-only columns in create form
      if (readOnlyColumns.includes(col.key)) return false;
      // Don't show actions column
      if (col.key === '_actions') return false;
      return true;
    });
  }, [columns, readOnlyColumns]);

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Create Form */}
      {showCreateForm && onCreate && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-blue-900">Créer un nouvel élément</h4>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setNewRowData({});
                setCreateErrors({});
              }}
              className="p-1 text-blue-600 hover:text-blue-700"
            >
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {createFormFields.map((column) => {
              const isRequired = requiredColumns.includes(column.key);
              const fieldError = createErrors[column.key];
              const columnType = getColumnType(column.key);

              return (
                <div key={column.key}>
                  <label htmlFor={`create-${column.key}`} className="block text-sm font-medium text-gray-700 mb-1">
                    {column.label}
                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {columnType === 'select' ? (
                    <select
                      id={`create-${column.key}`}
                      name={column.key}
                      value={newRowData[column.key] || ''}
                      onChange={(e) =>
                        setNewRowData({ ...newRowData, [column.key]: e.target.value })
                      }
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        fieldError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      {!isRequired && <option value="">-- Sélectionner --</option>}
                      {columnOptions[column.key]?.map((option) => (
                        <option key={String(option.value)} value={String(option.value)}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : columnType === 'date' ? (
                    <input
                      id={`create-${column.key}`}
                      name={column.key}
                      type="date"
                      value={newRowData[column.key] || ''}
                      onChange={(e) =>
                        setNewRowData({ ...newRowData, [column.key]: e.target.value })
                      }
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        fieldError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  ) : columnType === 'number' ? (
                    <input
                      type="number"
                      name={column.key}
                      value={newRowData[column.key] ?? ''}
                      onChange={(e) =>
                        setNewRowData({ ...newRowData, [column.key]: e.target.value })
                      }
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        fieldError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  ) : (
                    <input
                      type="text"
                      name={column.key}
                      value={newRowData[column.key] ?? ''}
                      onChange={(e) =>
                        setNewRowData({ ...newRowData, [column.key]: e.target.value })
                      }
                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        fieldError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  )}
                  {fieldError && (
                    <p className="mt-1 text-xs text-red-600">{fieldError}</p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => {
                setShowCreateForm(false);
                setNewRowData({});
                setCreateErrors({});
              }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Création...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Créer
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Create Button - can be shown/hidden via showCreateButton prop */}
      {showCreateButton && onCreate && !showCreateForm && (
        <div className="mb-4">
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Ajouter un élément
          </button>
        </div>
      )}

      {/* Table */}
      <RawDataTable
        {...rawTableProps}
        data={data}
        columns={columnsWithActions}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm.open}
        title="Confirmer la suppression"
        message={
          deleteConfirmMessage && showDeleteConfirm.row
            ? deleteConfirmMessage(showDeleteConfirm.row)
            : 'Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.'
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={() => {
          if (showDeleteConfirm.row) {
            handleDelete(showDeleteConfirm.row);
          }
        }}
        onClose={() => setShowDeleteConfirm({ open: false, row: null, rowId: '' })}
        confirmButtonClassName="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
        cancelButtonClassName="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
      />
    </>
  );
};

export default InlineEditTable;

